import React from "react";
import { Document, Link, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import {
  SectionDivider,
  commonStyles,
  formatDate,
  formatPhoneNumber,
  isValidSummary,
  parseHtmlToText,
  renderBulletPoints,
} from "../Services/ReactPDFService.jsx";

// Classic ATS Template Styles
const styles = StyleSheet.create({
  ...commonStyles,
  page: {
    ...commonStyles.page,
    backgroundColor: "#ffffff",
  },
  header: {
    marginBottom: 20,
    textAlign: "center",
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  name: {
    fontSize: 22,
    fontWeight: 700,
    marginBottom: 12,
    color: "#1a1a1a",
    letterSpacing: 0.5,
  },
  contactInfo: {
    fontSize: 9,
    color: "#555555",
    lineHeight: 1.3,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    marginTop: 15,
    marginBottom: 8,
    borderBottomWidth: 2,
    borderBottomColor: "#000000",
    paddingBottom: 6,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  experienceItem: {
    marginBottom: 15,
  },
  achievementItem: {
    marginBottom: 0,
  },
  experienceRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 2,
  },
  experienceLeft: {
    flex: 1,
    paddingRight: 20,
  },
  experienceRight: {
    alignItems: "flex-end",
    minWidth: 120,
  },
  jobTitle: {
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 2,
  },
  company: {
    fontSize: 10,
    fontStyle: "italic",
    marginBottom: 0,
  },
  experienceDate: {
    fontSize: 9,
    color: "#666666",
    textAlign: "right",
    marginBottom: 2,
  },
  experienceLocation: {
    fontSize: 9,
    color: "#666666",
    textAlign: "right",
  },
  dateLocation: {
    fontSize: 9,
    color: "#666666",
    marginBottom: 4,
  },
  description: {
    fontSize: 10,
    lineHeight: 1.4,
    marginBottom: 3,
  },
  bulletPoint: {
    fontSize: 10,
    marginBottom: 4,
    paddingLeft: 10,
    lineHeight: 1.4,
    color: "#333333",
  },
  educationItem: {
    marginBottom: 12,
  },
  educationRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 2,
  },
  educationLeft: {
    flex: 1,
    paddingRight: 20,
  },
  educationRight: {
    alignItems: "flex-end",
    minWidth: 120,
  },
  degree: {
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 2,
  },
  school: {
    fontSize: 10,
    fontStyle: "italic",
    marginBottom: 0,
  },
  educationDate: {
    fontSize: 10,
    color: "#666666",
    textAlign: "right",
    marginBottom: 2,
  },
  educationGPA: {
    fontSize: 10,
    color: "#666666",
    textAlign: "right",
  },
  skillsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  skillsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "flex-start",
  },
  skillItem: {
    flexDirection: "row",
    alignItems: "center",
    width: "33.33%",
    marginBottom: 4,
    paddingRight: 8,
  },
  skillBullet: {
    fontSize: 10,
    color: "#333",
    marginRight: 4,
    lineHeight: 1.4,
  },
  skillCategory: {
    marginBottom: 1,
    marginTop: 0,
  },
  skillLabel: {
    fontSize: 10,
    fontWeight: 700,
  },
  skillText: {
    fontSize: 10,
    lineHeight: 1.4,
  },
  projectItem: {
    marginBottom: 10,
  },
  projectTitle: {
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 2,
  },
  projectTechContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 3,
  },
  projectTechLabel: {
    fontSize: 9,
    fontStyle: "italic",
    color: "#666666",
    marginRight: 4,
  },
  projectTechList: {
    fontSize: 9,
    fontStyle: "italic",
    color: "#666666",
    flex: 1,
    lineHeight: 1.4,
  },
  projectTech: {
    fontSize: 9,
    fontStyle: "italic",
    color: "#666666",
    marginBottom: 3,
  },
});

// Helper function to safely extract skill name from string or object
const getSkillName = (skill) => {
  if (typeof skill === "string") return skill;
  if (skill && typeof skill === "object") return skill.name || skill.skill || "";
  return "";
};

// Helper function to join skills array (handles both strings and objects)
const joinSkills = (skillsArray) => {
  if (!Array.isArray(skillsArray)) return "";
  return skillsArray.map(getSkillName).filter(Boolean).join(", ");
};

// Helper to get language name from string or object
const getLanguageName = (lang) => {
  if (typeof lang === "string") return lang;
  if (lang && typeof lang === "object") return lang.name || lang.language || "";
  return "";
};

// Helper to get interest name from string or object
const getInterestName = (interest) => {
  if (typeof interest === "string") return interest;
  if (interest && typeof interest === "object") return interest.name || interest.title || "";
  return "";
};

const ClassicATSTemplate = ({ resumeData, customStyles = {}, customSectionOrder = null }) => {
  const {
    personalInfo = {},
    workExperience = [],
    education = [],
    skills = {},
    projects = [],
    certifications = [],
    achievements = [],
    languages = [],
    awards = [],
    summary = '',
    customSections = [],
    sectionTitles = {},
    // New sections
    volunteering = [],
    interests = [],
    industryExpertise = [],
    publications = [],
    references = [],
  } = resumeData || {};

  // Default section titles
  const defaultTitles = {
    summary: 'Professional Summary',
    experience: 'Professional Experience',
    education: 'Education',
    skills: 'Skills',
    projects: 'Projects',
    certifications: 'Certifications',
    achievements: 'Achievements',
    languages: 'Languages',
    awards: 'Awards',
    volunteering: 'Volunteering',
    interests: 'Interests',
    industryExpertise: 'Industry Expertise',
    publications: 'Publications',
    references: 'References',
  };

  // Get section title with fallback to default
  const getSectionTitle = (key) => sectionTitles[key] || defaultTitles[key];

  // Create dynamic styles based on customizations
  const createDynamicStyles = (baseStyles, customizations) => {
    // Only apply customizations if they exist, otherwise use original styles
    const hasCustomizations = customizations && Object.keys(customizations).length > 0;

    if (!hasCustomizations) {
      return baseStyles; // Return original styles unchanged
    }

    const { fontSize, sectionSpacing, titleColor, subtitleColor, fontFamily } = customizations;

    const customStyleOverrides = {};

    if (fontFamily) {
      customStyleOverrides.page = { ...baseStyles.page, fontFamily };
    }

    // Only override styles if specific customizations are provided
    if (fontSize) {
      customStyleOverrides.name = {
        ...baseStyles.name,
        fontSize: fontSize * 1.6,
      };
      customStyleOverrides.contactInfo = {
        ...baseStyles.contactInfo,
        fontSize: fontSize * 0.65,
      };
      customStyleOverrides.sectionTitle = {
        ...baseStyles.sectionTitle,
        fontSize: fontSize * 0.85,
      };
      customStyleOverrides.jobTitle = {
        ...baseStyles.jobTitle,
        fontSize: fontSize * 0.8,
      };
      customStyleOverrides.company = {
        ...baseStyles.company,
        fontSize: fontSize * 0.7,
      };
      customStyleOverrides.degree = {
        ...baseStyles.degree,
        fontSize: fontSize * 0.8,
      };
      customStyleOverrides.school = {
        ...baseStyles.school,
        fontSize: fontSize * 0.7,
      };
      customStyleOverrides.experienceDate = {
        ...baseStyles.experienceDate,
        fontSize: fontSize * 0.65,
      };
      customStyleOverrides.educationDate = {
        ...baseStyles.educationDate,
        fontSize: fontSize * 0.7,
      };
      customStyleOverrides.description = {
        ...baseStyles.description,
        fontSize: fontSize * 0.7,
      };
      customStyleOverrides.bulletPoint = {
        ...baseStyles.bulletPoint,
        fontSize: fontSize * 0.7,
      };
      customStyleOverrides.skillText = {
        ...baseStyles.skillText,
        fontSize: fontSize * 0.7,
      };
      customStyleOverrides.skillLabel = {
        ...baseStyles.skillLabel,
        fontSize: fontSize * 0.7,
      };
      customStyleOverrides.projectTitle = {
        ...baseStyles.projectTitle,
        fontSize: fontSize * 0.8,
      };
      customStyleOverrides.projectTechLabel = {
        ...baseStyles.projectTechLabel,
        fontSize: fontSize * 0.65,
      };
      customStyleOverrides.projectTechList = {
        ...baseStyles.projectTechList,
        fontSize: fontSize * 0.65,
      };
    }

    if (sectionSpacing) {
      customStyleOverrides.sectionTitle = {
        ...(customStyleOverrides.sectionTitle || baseStyles.sectionTitle),
        marginTop: sectionSpacing,
        marginBottom: sectionSpacing * 0.5,
      };
      customStyleOverrides.section = {
        ...baseStyles.section,
        marginBottom: sectionSpacing,
      };
    }

    if (titleColor) {
      customStyleOverrides.name = {
        ...(customStyleOverrides.name || baseStyles.name),
        color: titleColor,
      };
      customStyleOverrides.sectionTitle = {
        ...(customStyleOverrides.sectionTitle || baseStyles.sectionTitle),
        borderBottomColor: titleColor,
        color: titleColor,
      };
    }

    if (subtitleColor) {
      customStyleOverrides.jobTitle = {
        ...(customStyleOverrides.jobTitle || baseStyles.jobTitle),
        color: subtitleColor,
      };
      customStyleOverrides.company = {
        ...(customStyleOverrides.company || baseStyles.company),
        color: subtitleColor,
      };
      customStyleOverrides.degree = {
        ...(customStyleOverrides.degree || baseStyles.degree),
        color: subtitleColor,
      };
      customStyleOverrides.school = {
        ...(customStyleOverrides.school || baseStyles.school),
        color: subtitleColor,
      };
      customStyleOverrides.skillLabel = {
        ...(customStyleOverrides.skillLabel || baseStyles.skillLabel),
        color: subtitleColor,
      };
      customStyleOverrides.projectTitle = {
        ...(customStyleOverrides.projectTitle || baseStyles.projectTitle),
        color: subtitleColor,
      };
    }

    return StyleSheet.create({
      ...baseStyles,
      ...customStyleOverrides,
    });
  };

  // Merge base styles with custom overrides
  const mergedStyles = createDynamicStyles(styles, customStyles);

  // Define section order and content
  const defaultSectionOrder = [
    'summary',
    'workExperience',
    'education',
    'skills',
    'projects',
    'certifications',
    'achievements',
    'languages',
    'awards',
    'volunteering',
    'interests',
    'industryExpertise',
    'publications',
    'references'
  ];

  // Build final section order - include custom sections that aren't in the order
  const customSectionIds = customSections
    .filter(cs => cs.id && cs.title && cs.items?.length > 0)
    .map(cs => cs.id);

  const baseSectionOrder = customSectionOrder || defaultSectionOrder;

  // Add custom section IDs that aren't already in the order
  const finalSectionOrder = [
    ...baseSectionOrder,
    ...customSectionIds.filter(id => !baseSectionOrder.includes(id))
  ];

  // Define section components
  const sectionComponents = {
    summary: () => summary && isValidSummary(summary) && (
      <View style={mergedStyles.section} wrap={false}>
        <Text style={mergedStyles.sectionTitle}>{getSectionTitle('summary')}</Text>
        <Text style={mergedStyles.description}>
          {parseHtmlToText(summary)}
        </Text>
      </View>
    ),

    workExperience: () => workExperience.length > 0 && (
      <View style={mergedStyles.section}>
        <Text style={mergedStyles.sectionTitle} minPresenceAhead={50}>{getSectionTitle('experience')}</Text>
        {workExperience.map((job, index) => (
          <View key={index} style={mergedStyles.experienceItem} wrap={false}>
            <View style={mergedStyles.experienceRow}>
              <View style={mergedStyles.experienceLeft}>
                <Text style={mergedStyles.jobTitle}>{job.jobTitle}</Text>
              </View>
              <View style={mergedStyles.experienceRight}>
                <Text style={mergedStyles.experienceDate}>
                  {formatDate(job.startDate)} -{" "}
                  {job.isCurrentJob || job.endDate === "Present" || job.endDate === "Current" || !job.endDate
                    ? "Present"
                    : formatDate(job.endDate)}
                </Text>
              </View>
              </View>
              <View style={mergedStyles.experienceRow}>
                <View style={mergedStyles.experienceLeft}>
                  <Text style={mergedStyles.company}>{job.company}</Text>
                </View>
                <View style={mergedStyles.experienceRight}>
                  {job.location && <Text style={mergedStyles.experienceLocation}>{job.location}</Text>}
                </View>
              </View>
            {job.description && (
              <View style={{ marginTop: 4 }}>
                {renderBulletPoints(parseHtmlToText(job.description), mergedStyles)}
              </View>
            )}
          </View>
        ))}
      </View>
    ),

    education: () => education.length > 0 && (
      <View style={mergedStyles.section}>
        <Text style={mergedStyles.sectionTitle} minPresenceAhead={50}>{getSectionTitle('education')}</Text>
        {education.map((edu, index) => (
          <View key={index} style={mergedStyles.educationItem} wrap={false}>
            <View style={mergedStyles.educationRow}>
              <View style={mergedStyles.educationLeft}>
                <Text style={mergedStyles.degree}>
                  {edu.degree} {edu.fieldOfStudy && `in ${edu.fieldOfStudy}`}
                </Text>
              </View>
              <View style={mergedStyles.educationRight}>
                <Text style={mergedStyles.educationDate}>
                  {formatDate(edu.startDate)} - {formatDate(edu.endDate)}
                </Text>
              </View>
            </View>
            <View style={mergedStyles.educationRow}>
              <View style={mergedStyles.educationLeft}>
                <Text style={mergedStyles.school}>
                  {edu.school}{edu.location && `, ${edu.location}`}
                </Text>
              </View>
              <View style={mergedStyles.educationRight}>
                {edu.gpa && (
                  <Text style={mergedStyles.educationGPA}>GPA: {edu.gpa}</Text>
                )}
              </View>
            </View>
          </View>
        ))}
      </View>
    ),

    skills: () => (skills.technical?.length > 0 || skills.soft?.length > 0 || skills.tools?.length > 0 || skills.other?.length > 0 || skills.languages?.length > 0 || skills.frameworks?.length > 0 || skills.developer_tools?.length > 0 || skills.list?.length > 0) && (
      <View style={mergedStyles.section} wrap={false}>
        <Text style={mergedStyles.sectionTitle}>{getSectionTitle('skills')}</Text>
        {/* Check if we have categorized skills */}
        {(skills.technical?.length > 0 || skills.soft?.length > 0 || skills.tools?.length > 0 || skills.other?.length > 0 || skills.languages?.length > 0 || skills.frameworks?.length > 0 || skills.developer_tools?.length > 0) ? (
          <View>
            {skills.technical?.length > 0 && (
              <View style={mergedStyles.skillCategory}>
                <Text style={mergedStyles.skillText}>
                  <Text style={mergedStyles.skillLabel}>Technical Skills: </Text>
                  {joinSkills(skills.technical)}
                </Text>
              </View>
            )}
            {skills.soft?.length > 0 && (
              <View style={mergedStyles.skillCategory}>
                <Text style={mergedStyles.skillText}>
                  <Text style={mergedStyles.skillLabel}>Soft Skills: </Text>
                  {joinSkills(skills.soft)}
                </Text>
              </View>
            )}
            {skills.tools?.length > 0 && (
              <View style={mergedStyles.skillCategory}>
                <Text style={mergedStyles.skillText}>
                  <Text style={mergedStyles.skillLabel}>Tools: </Text>
                  {joinSkills(skills.tools)}
                </Text>
              </View>
            )}
            {skills.languages?.length > 0 && (
              <View style={mergedStyles.skillCategory}>
                <Text style={mergedStyles.skillText}>
                  <Text style={mergedStyles.skillLabel}>Languages: </Text>
                  {joinSkills(skills.languages)}
                </Text>
              </View>
            )}
            {skills.frameworks?.length > 0 && (
              <View style={mergedStyles.skillCategory}>
                <Text style={mergedStyles.skillText}>
                  <Text style={mergedStyles.skillLabel}>Frameworks: </Text>
                  {joinSkills(skills.frameworks)}
                </Text>
              </View>
            )}
            {skills.developer_tools?.length > 0 && (
              <View style={mergedStyles.skillCategory}>
                <Text style={mergedStyles.skillText}>
                  <Text style={mergedStyles.skillLabel}>Developer Tools: </Text>
                  {joinSkills(skills.developer_tools)}
                </Text>
              </View>
            )}
            {skills.other?.length > 0 && (
              <View style={mergedStyles.skillCategory}>
                <Text style={mergedStyles.skillText}>
                  <Text style={mergedStyles.skillLabel}>Other: </Text>
                  {joinSkills(skills.other)}
                </Text>
              </View>
            )}
          </View>
        ) : (
          // Fallback to flat list if no categorized skills
          <View style={mergedStyles.skillsGrid}>
            {skills.list.map((skill, index) => (
              <View key={index} style={mergedStyles.skillItem}>
                <Text style={mergedStyles.skillBullet}>•</Text>
                <Text style={mergedStyles.skillText}>{getSkillName(skill)}</Text>
              </View>
            ))}
          </View>
        )}
      </View>
    ),

    projects: () => projects.length > 0 && (
      <View style={mergedStyles.section}>
        <Text style={mergedStyles.sectionTitle} minPresenceAhead={50}>{getSectionTitle('projects')}</Text>
        {projects.map((project, index) => (
          <View key={index} style={mergedStyles.projectItem} wrap={false}>
            <View style={{flexDirection: 'row', alignItems: 'center'}}>
              <Text style={mergedStyles.projectTitle}>{project.name}</Text>
              {(project.url || project.link) && (
                <Link
                  src={(project.url || project.link).startsWith('http') ?
                    (project.url || project.link) :
                    `https://${project.url || project.link}`
                  }
                  style={{
                    fontSize: mergedStyles.projectTechLabel.fontSize,
                    color: '#0066cc',
                    marginLeft: 4,
                    textDecoration: 'underline',
                    fontWeight: 'bold'
                  }}
                >
                  [LINK]
                </Link>
              )}
            </View>
            {project.technologies && (
              <View style={mergedStyles.projectTechContainer}>
                <Text style={mergedStyles.projectTechLabel}>Technologies:</Text>
                <Text style={mergedStyles.projectTechList}>{project.technologies}</Text>
              </View>
            )}
            {project.description && (
              <View style={{ marginTop: 4 }}>
                {renderBulletPoints(parseHtmlToText(project.description), mergedStyles)}
              </View>
            )}
          </View>
        ))}
      </View>
    ),

    certifications: () => certifications.length > 0 && (
      <View style={mergedStyles.section}>
        <Text style={mergedStyles.sectionTitle} minPresenceAhead={50}>{getSectionTitle('certifications')}</Text>
        {certifications.map((cert, index) => (
          <View key={index} style={{ flexDirection: 'row', marginBottom: 4 }} wrap={false}>
            <Text style={{ fontSize: 10, color: '#1a1a1a', marginRight: 6, lineHeight: 1.5 }}>•</Text>
            <Text style={{ fontSize: 10, color: '#333333', lineHeight: 1.5, flex: 1 }}>
              {cert.name}
              {(cert.authority || cert.issuer) && ` - ${cert.authority || cert.issuer}`}
              {cert.date && ` (${formatDate(cert.date)})`}
            </Text>
          </View>
        ))}
      </View>
    ),

    achievements: () => achievements.length > 0 && (
      <View style={mergedStyles.section} wrap={false}>
        <Text style={mergedStyles.sectionTitle}>{getSectionTitle('achievements')}</Text>
        {achievements.map((achievement, index) => (
          <View key={index} style={mergedStyles.achievementItem}>
            {achievement.description && (
              <View style={{ marginTop: 2 }}>
                {renderBulletPoints(parseHtmlToText(achievement.description), mergedStyles)}
              </View>
            )}
          </View>
        ))}
      </View>
    ),

    languages: () => languages.length > 0 && (
      <View style={mergedStyles.section} wrap={false}>
        <Text style={mergedStyles.sectionTitle}>{getSectionTitle('languages')}</Text>
        <View style={mergedStyles.skillsGrid}>
          {languages.map((lang, index) => (
            <View key={index} style={mergedStyles.skillItem}>
              <Text style={mergedStyles.skillBullet}>•</Text>
              <Text style={mergedStyles.skillText}>
                {getLanguageName(lang)}{lang.proficiency ? ` - ${lang.proficiency}` : ''}
              </Text>
            </View>
          ))}
        </View>
      </View>
    ),

    awards: () => awards.length > 0 && (
      <View style={mergedStyles.section}>
        <Text style={mergedStyles.sectionTitle} minPresenceAhead={50}>{getSectionTitle('awards')}</Text>
        {awards.map((award, index) => (
          <View key={index} style={mergedStyles.educationItem} wrap={false}>
            <View style={mergedStyles.educationRow}>
              <View style={mergedStyles.educationLeft}>
                <Text style={mergedStyles.degree}>{award.title}</Text>
              </View>
              <View style={mergedStyles.educationRight}>
                {award.date && (
                  <Text style={mergedStyles.educationDate}>
                    {formatDate(award.date)}
                  </Text>
                )}
              </View>
            </View>
            <View style={mergedStyles.educationRow}>
              <View style={mergedStyles.educationLeft}>
                {award.issuer && (
                  <Text style={mergedStyles.school}>{award.issuer}</Text>
                )}
              </View>
            </View>
            {award.description && (
              <View style={{ marginTop: 4 }}>
                {renderBulletPoints(parseHtmlToText(award.description), mergedStyles)}
              </View>
            )}
          </View>
        ))}
      </View>
    ),

    // Volunteering Section
    volunteering: () => volunteering.length > 0 && (
      <View style={mergedStyles.section}>
        <Text style={mergedStyles.sectionTitle} minPresenceAhead={50}>{getSectionTitle('volunteering')}</Text>
        {volunteering.map((vol, index) => (
          <View key={index} style={mergedStyles.experienceItem} wrap={false}>
            <View style={mergedStyles.experienceRow}>
              <View style={mergedStyles.experienceLeft}>
                <Text style={mergedStyles.jobTitle}>{vol.role}</Text>
              </View>
              <View style={mergedStyles.experienceRight}>
                <Text style={mergedStyles.experienceDate}>
                  {formatDate(vol.startDate)} - {vol.isCurrentRole ? 'Present' : formatDate(vol.endDate)}
                </Text>
              </View>
            </View>
            <View style={mergedStyles.experienceRow}>
              <View style={mergedStyles.experienceLeft}>
                <Text style={mergedStyles.company}>{vol.organization}</Text>
              </View>
              {vol.location && (
                <View style={mergedStyles.experienceRight}>
                  <Text style={mergedStyles.location}>{vol.location}</Text>
                </View>
              )}
            </View>
            {vol.description && (
              <View style={{ marginTop: 4 }}>
                {renderBulletPoints(parseHtmlToText(vol.description), mergedStyles)}
              </View>
            )}
          </View>
        ))}
      </View>
    ),

    // Interests Section
    interests: () => interests.length > 0 && (
      <View style={mergedStyles.section} wrap={false}>
        <Text style={mergedStyles.sectionTitle}>{getSectionTitle('interests')}</Text>
        <View style={mergedStyles.skillsGrid}>
          {interests.map((interest, index) => (
            <View key={index} style={mergedStyles.skillItem}>
              <Text style={mergedStyles.skillBullet}>•</Text>
              <Text style={mergedStyles.skillText}>{getInterestName(interest)}</Text>
            </View>
          ))}
        </View>
      </View>
    ),

    // Industry Expertise Section
    industryExpertise: () => industryExpertise.length > 0 && (
      <View style={mergedStyles.section} wrap={false}>
        <Text style={mergedStyles.sectionTitle}>{getSectionTitle('industryExpertise')}</Text>
        {industryExpertise.map((exp, index) => (
          <View key={index} style={{ flexDirection: 'row', marginBottom: 4 }}>
            <Text style={{ fontSize: 10, color: '#1a1a1a', marginRight: 6, lineHeight: 1.5 }}>•</Text>
            <Text style={{ fontSize: 10, color: '#333333', lineHeight: 1.5, flex: 1 }}>
              {exp.name}{exp.proficiency ? ` - ${exp.proficiency}` : ''}{exp.yearsOfExperience ? ` (${exp.yearsOfExperience})` : ''}
            </Text>
          </View>
        ))}
      </View>
    ),

    // Publications Section
    publications: () => publications.length > 0 && (
      <View style={mergedStyles.section}>
        <Text style={mergedStyles.sectionTitle} minPresenceAhead={50}>{getSectionTitle('publications')}</Text>
        {publications.map((pub, index) => (
          <View key={index} style={mergedStyles.educationItem} wrap={false}>
            <Text style={mergedStyles.degree}>{pub.title}</Text>
            {pub.authors && pub.authors.length > 0 && (
              <Text style={mergedStyles.school}>{Array.isArray(pub.authors) ? pub.authors.join(', ') : pub.authors}</Text>
            )}
            <Text style={mergedStyles.educationDate}>
              {pub.journal}{pub.date ? ` • ${formatDate(pub.date)}` : ''}
            </Text>
          </View>
        ))}
      </View>
    ),

    // References Section
    references: () => references.length > 0 && (
      <View style={mergedStyles.section}>
        <Text style={mergedStyles.sectionTitle} minPresenceAhead={50}>{getSectionTitle('references')}</Text>
        {references.map((ref, index) => (
          <View key={index} style={mergedStyles.educationItem} wrap={false}>
            <Text style={mergedStyles.degree}>{ref.name}</Text>
            <Text style={mergedStyles.school}>{ref.title}{ref.company ? ` at ${ref.company}` : ''}</Text>
            <Text style={mergedStyles.educationDate}>
              {[ref.email, ref.phone].filter(Boolean).join(' • ')}
            </Text>
          </View>
        ))}
      </View>
    ),

    // Dynamic custom sections - each custom section gets its own render function
    ...customSections.reduce((acc, customSection) => {
      if (customSection.id && customSection.title && customSection.items?.length > 0) {
        acc[customSection.id] = () => (
          <View style={mergedStyles.section} wrap={false}>
            <Text style={mergedStyles.sectionTitle}>{customSection.title}</Text>
            {customSection.items.map((item, itemIndex) => (
              <View key={itemIndex} style={mergedStyles.achievementItem}>
                {item.content && (
                  <View style={mergedStyles.descriptionContainer}>
                    {renderBulletPoints(item.content, mergedStyles.description)}
                  </View>
                )}
              </View>
            ))}
          </View>
        );
      }
      return acc;
    }, {})
  };

  return (
    <Document>
      <Page size="A4" style={mergedStyles.page} wrap={true}>
        {/* Header Section - Always stays at top */}
        <View style={mergedStyles.header}>
          <Text style={mergedStyles.name}>
            {personalInfo.firstName} {personalInfo.lastName}
          </Text>
          <Text style={mergedStyles.contactInfo}>
            {[
              personalInfo.phone && formatPhoneNumber(personalInfo.phone),
              personalInfo.email,
              personalInfo.location,
            ]
              .filter(Boolean)
              .join("  •  ")}
          </Text>
          {(personalInfo.linkedinUrl || personalInfo.githubUrl) && (
            <Text style={mergedStyles.contactInfo}>
              {[
                personalInfo.linkedinUrl && `LinkedIn: ${personalInfo.linkedinUrl.split("/").pop()}`,
                personalInfo.githubUrl && `GitHub: ${personalInfo.githubUrl.split("/").pop()}`,
              ]
                .filter(Boolean)
                .join("  •  ")}
            </Text>
          )}
        </View>

        {/* Render sections in custom order */}
        {finalSectionOrder.map((sectionId, index) => {
          const SectionComponent = sectionComponents[sectionId];
          return SectionComponent ? <View key={`${sectionId}-${index}`} wrap={true}>{SectionComponent()}</View> : null;
        })}
      </Page>
    </Document>
  );
};

export default ClassicATSTemplate;
